import { useI18n } from "@/hooks/useI18n";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { Label } from "@/components/ui/label";
import { DayAxis, isTemplateAxis } from "../models/day-axis";
import { DayField } from "./DayField";

/** I campi con cui un'attività dichiara la propria estensione nel tempo. */
export interface DayRangeValue {
  start_date?: string;
  end_date?: string;
  start_day_index?: number | null;
  end_day_index?: number | null;
}

interface DayRangeFieldProps {
  /** Asse dell'itinerario: decide se si scelgono date reali o giorni relativi. */
  axis?: DayAxis | null;
  value: DayRangeValue;
  /** Riceve solo i campi cambiati, da fondere nel form del chiamante. */
  onChange: (patch: DayRangeValue) => void;
  minDate?: string | Date;
  maxDate?: string | Date;
  hasError?: boolean;
  disabled?: boolean;
  /** Il giorno finale può eccedere la durata (check-out il giorno dopo l'ultima notte). */
  allowEndBeyondDuration?: boolean;
  /**
   * 'rows' impagina i due giorni come le righe degli orari — un solo box con
   * etichetta breve a sinistra — così data e orario stanno affiancati senza
   * sembrare due controlli di famiglie diverse.
   */
  variant?: 'default' | 'rows';
  /** Etichette delle due righe: il default è "Da"/"A". */
  labels?: { start: string; end: string };
}

/**
 * Intervallo di giorni di un'attività.
 *
 * Su un itinerario collegato a una travel request sono due date; su un template, che
 * di date non ne ha, sono due "Giorno N". Il componente conosce l'invariante che i
 * chiamanti sbagliavano a mano: su un template l'intervallo va sempre completo, quindi
 * spostare il giorno d'inizio si porta dietro quello di fine se resterebbe scoperto.
 */
export const DayRangeField = ({
  axis,
  value,
  onChange,
  minDate,
  maxDate,
  hasError = false,
  disabled = false,
  allowEndBeyondDuration = false,
  variant = 'default',
  labels,
}: DayRangeFieldProps) => {
  const { t } = useI18n();

  const startLabel = labels?.start ?? String(t('common.from'));
  const endLabel = labels?.end ?? String(t('common.to'));

  if (variant === 'rows') {
    // L'etichetta della riga dice già "Da"/"A": il placeholder del controllo non
    // la ripete, mostra il formato atteso (o "Giorno —" sui template).
    const emptyLabel = String(isTemplateAxis(axis) ? t('modals.dayPlaceholder') : t('modals.datePlaceholder'));
    // Il bordo è del box: i due controlli dentro lo perdono, come gli input orario.
    const inner = 'h-8 border-0 px-0 shadow-none hover:bg-transparent focus:ring-0 focus-visible:ring-0';
    return (
      <div
        className={`divide-y divide-gray-200 rounded-md border ${hasError ? 'border-yellow-400' : 'border-input'}`}
      >
        <div className="flex h-9 items-center gap-1.5 px-2.5">
          <span className="w-16 shrink-0 text-[11px] text-gray-500">{startLabel}</span>
          <div className="min-w-0 flex-1">
            <DayField
              axis={axis}
              date={value.start_date}
              dayIndex={value.start_day_index}
              onDateChange={(start_date) => onChange({ start_date })}
              onDayIndexChange={(index) =>
                onChange({
                  start_day_index: index,
                  end_day_index:
                    index && value.end_day_index && value.end_day_index >= index
                      ? value.end_day_index
                      : index,
                })
              }
              placeholder={emptyLabel}
              minDate={minDate}
              maxDate={maxDate}
              disabled={disabled}
              className={inner}
            />
          </div>
        </div>
        <div className="flex h-9 items-center gap-1.5 px-2.5">
          <span className="w-16 shrink-0 text-[11px] text-gray-500">{endLabel}</span>
          <div className="min-w-0 flex-1">
            <DayField
              axis={axis}
              date={value.end_date}
              dayIndex={value.end_day_index}
              onDateChange={(end_date) => onChange({ end_date })}
              onDayIndexChange={(end_day_index) => onChange({ end_day_index })}
              placeholder={emptyLabel}
              // Il giorno finale non può precedere quello iniziale.
              minDate={value.start_date || minDate}
              maxDate={maxDate}
              minIndex={value.start_day_index || 1}
              extraDays={allowEndBeyondDuration ? 1 : 0}
              disabled={disabled}
              className={inner}
            />
          </div>
        </div>
      </div>
    );
  }

  // Itinerario datato: resta il range picker di sempre, con il suo calendario collegato.
  if (!isTemplateAxis(axis)) {
    return (
      <DateRangePicker
        startDate={value.start_date || ''}
        endDate={value.end_date || ''}
        onStartDateChange={(start_date) => onChange({ start_date })}
        onEndDateChange={(end_date) => onChange({ end_date })}
        startDatePlaceholder={t('common.from')}
        endDatePlaceholder={t('common.to')}
        hasError={hasError}
        minDate={typeof minDate === 'string' ? minDate : undefined}
        maxDate={typeof maxDate === 'string' ? maxDate : undefined}
        disabled={disabled}
      />
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1">
        <Label className="text-xs text-gray-500">{t('common.from')}</Label>
        <DayField
          axis={axis}
          date={value.start_date}
          dayIndex={value.start_day_index}
          onDateChange={(start_date) => onChange({ start_date })}
          onDayIndexChange={(index) =>
            onChange({
              start_day_index: index,
              // L'intervallo non resta mai a metà: senza una fine valida vale il giorno stesso.
              end_day_index:
                index && value.end_day_index && value.end_day_index >= index
                  ? value.end_day_index
                  : index,
            })
          }
          placeholder={t('common.from')}
          minDate={minDate}
          maxDate={maxDate}
          hasError={hasError}
          disabled={disabled}
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs text-gray-500">{t('common.to')}</Label>
        <DayField
          axis={axis}
          date={value.end_date}
          dayIndex={value.end_day_index}
          onDateChange={(end_date) => onChange({ end_date })}
          onDayIndexChange={(end_day_index) => onChange({ end_day_index })}
          placeholder={t('common.to')}
          // Il giorno finale non può precedere quello iniziale.
          minDate={value.start_date || minDate}
          maxDate={maxDate}
          minIndex={value.start_day_index || 1}
          extraDays={allowEndBeyondDuration ? 1 : 0}
          hasError={hasError}
          disabled={disabled}
        />
      </div>
    </div>
  );
};
